import Cookies from "js-cookie";
export interface RawCookie {
  name: string;
  value: string;
}

function _getCookies(): RawCookie[] {
  // Apparently few libraries handle duplicate cookies
  // Cookies are identified by the triplet (name, domain, path)
  // If two conflict, the newer overwrites
  // but you can still have cookies with the same name if they differ by domain
  // however only the name is accessible from JS
  return document.cookie
    .split("; ")
    .filter(Boolean)
    .map((pair) => {
      const [name, value] = pair.split("=", 2);
      return { name, value:value };
    });
}

function _getCookieCount(predicate ?: (cookie: RawCookie) => boolean): number {
  return _getCookies().filter((c) => !predicate || predicate(c)).length;
}

export class SupabaseCookie {
  constructor(readonly name: string, readonly values: string[]) {}

  delete(domain?: string): boolean {
    // We can't actually read the domain attribute from JS
    // Instead we can delete a cookie by name and domain, and see if the count decreases
    const count = _getCookieCount();
    Cookies.remove(this.name, domain ? { domain } : {});
    return _getCookieCount() < count;
  }

  get isChunked() {
    // Chunked superbase cookies end with .1, .2, etc. and share the same prefix
    // They're used due to cookie size limits. When parsed, they're just concatenated.
    return this.name.match(/.(\d+)$/)
  }
}

export class SupabaseCookieManager {
  private readonly prefix: string;

  constructor(projectRef: string) {
    this.prefix = `sb-${projectRef}-auth-token`;
  }

  static fromURL(url: string): SupabaseCookieManager {
    const ref = new URL(url).hostname.split(".")[0];
    return new SupabaseCookieManager(ref);
  }

  private get _rawSbCookies() {
    return _getCookies().filter((c) => c.name.startsWith(this.prefix));
  }

  get(): SupabaseCookie[] {
    const cookies = this._rawSbCookies;
    if (cookies.length === 0) return [];

    const grouped = Map.groupBy(cookies, (c) => c.name);

    // We unify cookies with the same name for processing
    return [...grouped.entries()].map(([name, cookies]) => {
      return new SupabaseCookie(
        name,
        cookies.map((c) => c.value),
      );
    });
  }

  autoCleanUnneededAnonCookies() {
    const sbCookies = this.get();
    if (sbCookies.length <= 1) {
      // No duplicate cookies so nothing to do
      return
    }
    for (const cookie of sbCookies) {
      if (window.location.hostname !== "localhost") {
        // In non-local environments, login cookies differ from anon cookies by domain
        // so we can just delete all cookies with an empty domain.
        cookie.delete()
      } else if (!cookie.isChunked) {
        // On localhost, domain is always empty., so we rely on something not as good: 
        // login cookies always seem to be chunked while anon cookies aren't.
        // So we delete all non-chunked cookies.
        cookie.delete()
      }      
    }
  }
}
